import { useState } from 'react';
import { CreditTransaction } from '../../lib/api';

interface TransactionHistoryProps {
  transactions: CreditTransaction[];
  total: number;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

// =============================================================================
// Formatting Helpers
// =============================================================================

function formatCreditAmount(credits: number): string {
  const abs = Math.abs(credits);
  if (abs === 0) return '0';
  if (Number.isInteger(credits)) return credits.toLocaleString();
  if (abs >= 0.01) return credits.toFixed(2);
  return credits.toFixed(4);
}

function getTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    grant: 'Credit Grant',
    system_grant: 'System Credits',
    purchase: 'Purchase',
    subscription_credit: 'Subscription',
    usage: 'Usage',
    refund: 'Refund',
    adjustment: 'Adjustment',
    storage_charge: 'Storage',
    file_operation: 'File Op',
    api_usage: 'API Usage',
  };
  return labels[type] ?? type;
}

function getTypeStyles(type: string): string {
  switch (type) {
    case 'grant':
    case 'system_grant':
    case 'purchase':
    case 'subscription_credit':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'usage':
    case 'storage_charge':
    case 'file_operation':
    case 'api_usage':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 'refund':
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function getCategoryLabel(category: string | null): string {
  if (!category) return '';
  const labels: Record<string, string> = {
    ai_chat: 'AI Chat',
    ai_multi: 'Multi-AI',
    ai_image: 'Image Gen',
    ai_builder: 'Plugin Builder',
    ai_social: 'Social Media AI',
    ai_learn: 'Learning',
    ai_calendar: 'Calendar AI',
    ai_other: 'AI',
    storage: 'Storage',
    file_ops: 'Files',
    publishing: 'Publishing',
    api_external: 'External API',
  };
  return labels[category] ?? category;
}

// =============================================================================
// Data Grouping
// =============================================================================

interface CollapsedTransaction {
  id: string;
  type: string;
  netAmount: number;
  balanceAfter: number;
  description: string;
  createdAt: string;
  requestId: string | null;
  expenseCategory: string | null;
  sessionId: string | null;
  rawEntries: CreditTransaction[];
}

interface SessionGroup {
  sessionId: string;
  label: string;
  category: string | null;
  totalAmount: number;
  startedAt: string;
  transactions: CollapsedTransaction[];
}

function collapseReserveRefundPairs(transactions: CreditTransaction[]): CollapsedTransaction[] {
  const result: CollapsedTransaction[] = [];
  const refundsByRequestId = new Map<string, CreditTransaction>();

  // Pass 1: collect finalization refunds keyed by requestId
  for (const txn of transactions) {
    if (txn.type === 'refund' && txn.requestId && txn.description?.includes('finalization')) {
      refundsByRequestId.set(txn.requestId, txn);
    }
  }

  // Precompute which requestIds have a matching usage in this batch so we know
  // which finalization refunds will be consumed by a collapsed entry.
  const usageRequestIds = new Set<string>();
  for (const txn of transactions) {
    if (txn.type === 'usage' && txn.requestId) {
      usageRequestIds.add(txn.requestId);
    }
  }

  const consumedRefundIds = new Set<string>();
  for (const [requestId, refund] of refundsByRequestId) {
    if (usageRequestIds.has(requestId)) {
      consumedRefundIds.add(refund.transactionId);
    }
  }

  // Pass 2: build collapsed/standalone entries preserving original order
  for (const txn of transactions) {
    if (txn.type === 'usage' && txn.requestId && refundsByRequestId.has(txn.requestId)) {
      const refund = refundsByRequestId.get(txn.requestId)!;

      let description = txn.description ?? '';
      // Clean up internal status suffixes for display
      description = description.replace(/\s*\((reserved|finalized)[^)]*\)/, '');

      result.push({
        id: txn.transactionId,
        type: 'usage',
        netAmount: txn.amount + refund.amount,
        balanceAfter: refund.balanceAfter,
        description,
        createdAt: txn.createdAt,
        requestId: txn.requestId,
        expenseCategory: txn.expenseCategory,
        sessionId: (txn.metadata?.sessionId as string) ?? null,
        rawEntries: [txn, refund],
      });
    } else if (consumedRefundIds.has(txn.transactionId)) {
      // Skip — this finalization refund is included in its matching collapsed entry
      continue;
    } else {
      result.push({
        id: txn.transactionId,
        type: txn.type,
        netAmount: txn.amount,
        balanceAfter: txn.balanceAfter,
        description: txn.description ?? '',
        createdAt: txn.createdAt,
        requestId: txn.requestId,
        expenseCategory: txn.expenseCategory,
        sessionId: (txn.metadata?.sessionId as string) ?? null,
        rawEntries: [txn],
      });
    }
  }
  return result;
}

function groupIntoSessions(
  collapsed: CollapsedTransaction[],
): (SessionGroup | CollapsedTransaction)[] {
  const result: (SessionGroup | CollapsedTransaction)[] = [];
  const sessionMap = new Map<string, CollapsedTransaction[]>();
  const order: (string | CollapsedTransaction)[] = [];
  const seenSessions = new Set<string>();

  for (const txn of collapsed) {
    if (txn.sessionId) {
      if (!sessionMap.has(txn.sessionId)) {
        sessionMap.set(txn.sessionId, []);
      }
      sessionMap.get(txn.sessionId)!.push(txn);
      if (!seenSessions.has(txn.sessionId)) {
        seenSessions.add(txn.sessionId);
        order.push(txn.sessionId);
      }
    } else {
      order.push(txn);
    }
  }

  for (const item of order) {
    if (typeof item === 'string') {
      const txns = sessionMap.get(item)!;
      const totalAmount = txns.reduce((sum, t) => sum + t.netAmount, 0);
      const category = txns[0].expenseCategory;
      const categoryLabel = getCategoryLabel(category);
      const label = categoryLabel ? `${categoryLabel} Session` : `Session`;

      result.push({
        sessionId: item,
        label,
        category,
        totalAmount,
        startedAt: txns[0].createdAt,
        transactions: txns,
      });
    } else {
      result.push(item);
    }
  }

  return result;
}

function isSessionGroup(item: SessionGroup | CollapsedTransaction): item is SessionGroup {
  return 'transactions' in item && 'label' in item;
}

// =============================================================================
// Sub-Components
// =============================================================================

function getRawEntryLabel(txn: CreditTransaction, isCollapsedChild: boolean): string {
  if (!isCollapsedChild) return getTypeLabel(txn.type);
  // Inside a collapsed reserve/refund pair, use clearer labels
  if (txn.type === 'usage') return 'Estimate';
  if (txn.type === 'refund') return 'Adjustment';
  return getTypeLabel(txn.type);
}

function RawEntryRow({
  txn,
  isCollapsedChild = false,
}: {
  txn: CreditTransaction;
  isCollapsedChild?: boolean;
}) {
  const label = getRawEntryLabel(txn, isCollapsedChild);
  return (
    <div className="flex items-center justify-between py-1.5 text-xs text-muted-foreground">
      <div className="flex items-center gap-2">
        <span
          className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getTypeStyles(txn.type)}`}
        >
          {label}
        </span>
        <span>{new Date(txn.createdAt).toLocaleTimeString()}</span>
      </div>
      <span
        className={
          txn.amount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
        }
      >
        {txn.amount >= 0 ? '+' : ''}
        {formatCreditAmount(txn.amount)}
      </span>
    </div>
  );
}

function TransactionRow({ txn }: { txn: CollapsedTransaction }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = txn.rawEntries.length > 1;

  return (
    <div className="py-2">
      <button
        onClick={() => hasDetail && setExpanded((v) => !v)}
        className={`w-full flex items-start justify-between gap-4 text-left ${hasDetail ? 'cursor-pointer hover:bg-muted/30 -mx-2 px-2 rounded' : ''}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`px-2 py-0.5 text-xs font-medium rounded whitespace-nowrap ${getTypeStyles(txn.type)}`}
            >
              {getTypeLabel(txn.type)}
            </span>
            <span className="text-xs text-muted-foreground">
              {new Date(txn.createdAt).toLocaleTimeString()}
            </span>
            {hasDetail && (
              <span className="text-[10px] text-muted-foreground/60">
                {expanded ? '[-]' : '[+]'}
              </span>
            )}
          </div>
          {txn.description && (
            <p className="text-sm text-muted-foreground truncate mt-0.5">{txn.description}</p>
          )}
        </div>
        <span
          className={`text-sm font-semibold shrink-0 ${txn.netAmount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
        >
          {txn.netAmount >= 0 ? '+' : ''}
          {formatCreditAmount(txn.netAmount)}
        </span>
      </button>
      {expanded && (
        <div className="ml-6 mt-1 pl-3 border-l-2 border-border/50">
          {txn.rawEntries.map((raw) => (
            <RawEntryRow key={raw.transactionId} txn={raw} isCollapsedChild />
          ))}
        </div>
      )}
    </div>
  );
}

function SessionGroupRow({ session }: { session: SessionGroup }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="p-4 hover:bg-muted/50">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-start justify-between gap-4 text-left"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground">{session.label}</span>
            <span className="text-xs text-muted-foreground">
              {session.transactions.length} {session.transactions.length === 1 ? 'call' : 'calls'}
            </span>
            <span className="text-xs text-muted-foreground">
              {new Date(session.startedAt).toLocaleString()}
            </span>
            <span className="text-[10px] text-muted-foreground/60">{expanded ? '[-]' : '[+]'}</span>
          </div>
        </div>
        <span
          className={`text-sm font-semibold shrink-0 ${session.totalAmount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
        >
          {session.totalAmount >= 0 ? '+' : ''}
          {formatCreditAmount(session.totalAmount)}
        </span>
      </button>
      {expanded && (
        <div className="ml-4 mt-2 pl-3 border-l-2 border-border/50 space-y-0.5">
          {session.transactions.map((txn) => (
            <TransactionRow key={txn.id} txn={txn} />
          ))}
        </div>
      )}
    </div>
  );
}

function StandaloneRow({ txn }: { txn: CollapsedTransaction }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = txn.rawEntries.length > 1;

  return (
    <div className="p-4 hover:bg-muted/50">
      <button
        onClick={() => hasDetail && setExpanded((v) => !v)}
        className={`w-full flex items-start justify-between gap-4 text-left ${hasDetail ? 'cursor-pointer' : ''}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`px-2 py-0.5 text-xs font-medium rounded whitespace-nowrap ${getTypeStyles(txn.type)}`}
            >
              {getTypeLabel(txn.type)}
            </span>
            {txn.expenseCategory && (
              <span className="text-xs text-muted-foreground">
                {getCategoryLabel(txn.expenseCategory)}
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {new Date(txn.createdAt).toLocaleString()}
            </span>
            {hasDetail && (
              <span className="text-[10px] text-muted-foreground/60">
                {expanded ? '[-]' : '[+]'}
              </span>
            )}
          </div>
          {txn.description && (
            <p className="text-sm text-muted-foreground truncate mt-0.5">{txn.description}</p>
          )}
        </div>
        <div className="text-right shrink-0">
          <span
            className={`text-sm font-semibold ${txn.netAmount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
          >
            {txn.netAmount >= 0 ? '+' : ''}
            {formatCreditAmount(txn.netAmount)}
          </span>
          <div className="text-xs text-muted-foreground">
            Bal: {formatCreditAmount(txn.balanceAfter)}
          </div>
        </div>
      </button>
      {expanded && (
        <div className="ml-6 mt-1 pl-3 border-l-2 border-border/50">
          {txn.rawEntries.map((raw) => (
            <RawEntryRow key={raw.transactionId} txn={raw} isCollapsedChild />
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function TransactionHistory({
  transactions,
  total,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
}: TransactionHistoryProps) {
  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-muted rounded w-48 mb-4"></div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="py-4 border-b border-border last:border-0">
              <div className="flex justify-between items-center">
                <div className="h-4 bg-muted rounded w-32"></div>
                <div className="h-4 bg-muted rounded w-16"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const collapsed = collapseReserveRefundPairs(transactions);
  const grouped = groupIntoSessions(collapsed);

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
      <div className="p-6 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">
          Transaction History
          <span className="text-sm font-normal text-muted-foreground ml-2">({total} total)</span>
        </h2>
      </div>

      {grouped.length === 0 ? (
        <div className="p-6 text-center text-muted-foreground">No transactions yet</div>
      ) : (
        <div className="divide-y divide-border">
          {grouped.map((item) =>
            isSessionGroup(item) ? (
              <SessionGroupRow key={item.sessionId} session={item} />
            ) : (
              <StandaloneRow key={item.id} txn={item} />
            ),
          )}
        </div>
      )}

      {hasMore && (
        <div className="p-4 border-t border-border text-center">
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="px-4 py-2 text-sm font-medium text-primary hover:text-primary/80 disabled:text-muted-foreground"
          >
            {loadingMore ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
}
